/*
 * CAPTURE-OWNED MODULE
 * Zone Editor Overlay Component for capture-ui
 * Renders region/anchor/field zones as colored boxes on the PDF viewer.
 * Supports Draw mode (click+drag to create), Select mode (click to select,
 * drag to move, corner handles to resize), keyboard delete, a right-click
 * "Delete zone" menu, and optional OCR-word snap-assist on mouse-up. Each zone
 * is labelled with its field and type; anchor and region zones are dimmed
 * while dimAnchorRegion is set, and their labels then show only for the
 * selected field, the selected zone or the hovered zone (utils/zoneLabels).
 *
 * Paint order is largest area first and the selected zone last
 * (zonePaintOrder), frozen while a zone is being moved or resized, so the
 * smallest zone under the pointer wins. Table and region zones are outlines:
 * their inside takes no pointer events, and they are picked by a 6px edge
 * strip or their label tab. In a pick mode (pickMode) no zone takes pointer
 * events and every zone but the placed field's is dimmed, so a click on a
 * word inside a zone reaches the word.
 *
 * Every gesture (draw, band, move, resize) tells the hosting viewer it is live
 * through DragScrollContext, so the viewer scrolls when the pointer nears its
 * edge. Positions are always measured against this page's box at the moment of
 * the event, never cached in client pixels, so a gesture stays put in
 * document space while the pages scroll under a still pointer.
 */

import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { DragScrollContext } from '../utils/dragAutoScroll';
import { ZONE_STYLES, zoneLabel, isZoneDimmed, isZoneLabelShown, isOutlineZone, zonePaintOrder, DIMMED_OPACITY } from '../utils/zoneLabels';

const HANDLE_SIZE = 10;
const EDGE_SIZE = 6; // px, the clickable strip along an outline zone's border
const MIN_ZONE = 0.01; // minimum normalized dimension
const SNAP_THRESHOLD = 0.005; // ~6px at typical zoom

// Handle positions: [name, cssProp for left/right, cssProp for top/bottom, cursor]
const HANDLE_DEFS = [
  { name: 'tl', hProp: 'left',  vProp: 'top',    cursor: 'nwse-resize' },
  { name: 'tr', hProp: 'right', vProp: 'top',    cursor: 'nesw-resize' },
  { name: 'bl', hProp: 'left',  vProp: 'bottom', cursor: 'nesw-resize' },
  { name: 'br', hProp: 'right', vProp: 'bottom', cursor: 'nwse-resize' },
];

// The four edge strips of an outline zone, centred on its border.
const EDGE_DEFS = [
  { name: 'top',    style: { left: -EDGE_SIZE / 2, right: -EDGE_SIZE / 2, top: -EDGE_SIZE / 2, height: EDGE_SIZE } },
  { name: 'bottom', style: { left: -EDGE_SIZE / 2, right: -EDGE_SIZE / 2, bottom: -EDGE_SIZE / 2, height: EDGE_SIZE } },
  { name: 'left',   style: { top: -EDGE_SIZE / 2, bottom: -EDGE_SIZE / 2, left: -EDGE_SIZE / 2, width: EDGE_SIZE } },
  { name: 'right',  style: { top: -EDGE_SIZE / 2, bottom: -EDGE_SIZE / 2, right: -EDGE_SIZE / 2, width: EDGE_SIZE } },
];

/** Clamp a zone rect to [0..1] with minimum size */
function clampZone(x, y, w, h) {
  let cw = Math.max(MIN_ZONE, w);
  let ch = Math.max(MIN_ZONE, h);
  let cx = Math.max(0, Math.min(x, 1 - cw));
  let cy = Math.max(0, Math.min(y, 1 - ch));
  // re-clamp w/h if x/y got pushed
  cw = Math.min(cw, 1 - cx);
  ch = Math.min(ch, 1 - cy);
  return { x: cx, y: cy, w: cw, h: ch };
}

/** Snap a single edge value to the nearest OCR word boundary within threshold */
function snapEdge(val, edges) {
  let best = val;
  let bestDist = SNAP_THRESHOLD;
  for (const e of edges) {
    const d = Math.abs(val - e);
    if (d < bestDist) {
      bestDist = d;
      best = e;
    }
  }
  return best;
}

/** Snap zone edges to nearby OCR word boundaries */
export function snapToOcrWords(zone, ocrWords) {
  if (!ocrWords || ocrWords.length === 0) return zone;

  // Collect all unique x and y edges from OCR word bboxes
  const xEdges = [];
  const yEdges = [];
  for (const w of ocrWords) {
    const b = w.bbox || w;
    if (b.x1 != null) { xEdges.push(b.x1); xEdges.push(b.x2); }
    if (b.y1 != null) { yEdges.push(b.y1); yEdges.push(b.y2); }
  }

  const x1 = snapEdge(zone.x, xEdges);
  const y1 = snapEdge(zone.y, yEdges);
  const x2 = snapEdge(zone.x + zone.w, xEdges);
  const y2 = snapEdge(zone.y + zone.h, yEdges);

  return clampZone(x1, y1, x2 - x1, y2 - y1);
}

// A press in Click-a-word mode that moves farther than this draws a region.
export const DRAG_TO_DRAW_PX = 4;

/**
 * Preview box for a draw in progress. Not clamped up to the minimum size: the
 * preview renders only once the box is at least MIN_ZONE both ways, which is
 * exactly when releasing creates a zone (see drawnZone).
 */
export function previewRect(start, norm) {
  if (!norm) return null;
  return {
    x: Math.min(start.x, norm.x), y: Math.min(start.y, norm.y),
    w: Math.abs(norm.x - start.x), h: Math.abs(norm.y - start.y),
  };
}

/**
 * The zone a finished draw creates, or null when the box is smaller than
 * MIN_ZONE in either direction. Snaps to OCR word edges when words are given.
 */
export function drawnZone(start, norm, ocrWords) {
  if (!start || !norm) return null;
  const w = Math.abs(norm.x - start.x);
  const h = Math.abs(norm.y - start.y);
  if (w < MIN_ZONE || h < MIN_ZONE) return null;
  const zone = clampZone(Math.min(start.x, norm.x), Math.min(start.y, norm.y), w, h);
  return ocrWords && ocrWords.length > 0 ? snapToOcrWords(zone, ocrWords) : zone;
}

// Same padding the word-click path puts around a picked word.
export const WORD_PAD = 0.005;

/**
 * Click-a-word drag band: the dragged box, thickened to at least MIN_ZONE in
 * each direction around its center so a flat sweep along a line still has
 * height. Kept inside the page.
 */
export function bandRect(start, norm) {
  if (!start || !norm) return null;
  const x1 = Math.min(start.x, norm.x);
  const y1 = Math.min(start.y, norm.y);
  const w = Math.abs(norm.x - start.x);
  const h = Math.abs(norm.y - start.y);
  const bw = Math.max(MIN_ZONE, w);
  const bh = Math.max(MIN_ZONE, h);
  return clampZone(x1 + w / 2 - bw / 2, y1 + h / 2 - bh / 2, bw, bh);
}

/**
 * The zone a Click-a-word drag creates. Every OCR word whose box intersects
 * the band (bandRect) is taken; the zone is the union of their boxes padded by
 * WORD_PAD and clamped to the page, the same arithmetic as the word-click
 * path, so a band touching one word gives that word's click box. With no word
 * touched it falls back to the draw rule (drawnZone).
 */
export function bandWordsZone(start, norm, ocrWords) {
  const band = bandRect(start, norm);
  if (!band) return null;
  const bx2 = band.x + band.w;
  const by2 = band.y + band.h;
  const hit = (ocrWords || [])
    .map((w) => w.bbox || w)
    .filter((b) => b && b.x1 != null && b.x1 < bx2 && b.x2 > band.x && b.y1 < by2 && b.y2 > band.y);
  if (hit.length === 0) return drawnZone(start, norm, ocrWords);
  const ux1 = Math.min(...hit.map((b) => b.x1));
  const uy1 = Math.min(...hit.map((b) => b.y1));
  const ux2 = Math.max(...hit.map((b) => b.x2));
  const uy2 = Math.max(...hit.map((b) => b.y2));
  const x = Math.max(0, ux1 - WORD_PAD);
  const y = Math.max(0, uy1 - WORD_PAD);
  return {
    x,
    y,
    w: Math.min(1 - x, (ux2 - ux1) + WORD_PAD * 2),
    h: Math.min(1 - y, (uy2 - uy1) + WORD_PAD * 2),
  };
}

/**
 * The words a gesture on page `pageIndex` (0-based) may use. A word tagged
 * with another page_index is dropped: the editor's word list is fetched per
 * active page and can still hold the previous page's words right after a page
 * switch. Untagged words are kept (callers that never mix pages).
 */
export function wordsOnPage(ocrWords, pageIndex) {
  if (!ocrWords) return ocrWords;
  return ocrWords.filter((w) => w.page_index == null || w.page_index === pageIndex);
}

// How long a finished draw waits for its page's words before it is resolved
// without them.
export const WORDS_WAIT_MS = 5000;

const ZoneEditorOverlay = ({
  selectedField,
  zones = [],
  onZoneCommit,
  onZoneDelete,
  onZoneCreate,
  currentPageNumber,
  fields = [],
  onSelectField,
  editingZoneId,
  onEditingZoneIdChanged,
  // Draw/Select mode: 'select' | 'draw'
  mode = 'select',
  // Default zone_type + field_id for newly drawn zones
  drawZoneType = 'field',
  drawFieldId = null,
  // OCR words for snap-assist (array of { bbox: {x1,y1,x2,y2}, text, page_index }).
  // A gesture uses only the words of the page it started on (wordsOnPage).
  // null means the page's words have not loaded.
  ocrWords = null,
  // Optional (pageIndex) => Promise<words | null>. A draw that ends while its
  // page's words are null waits for this (at most WORDS_WAIT_MS) before it
  // resolves the zone, so a press on a page that has just loaded never acts
  // on an empty word list.
  loadWords = null,
  // pageSurfaceRef passed by SimplePDFViewer clone
  pageSurfaceRef,
  // Callback to switch mode (e.g., auto-switch to 'select' after draw)
  onModeSwitch,
  // When true, the entire overlay passes pointer events through to layers beneath.
  // Used by fieldPickMode in TemplateEditorPage so OcrWordOverlay receives clicks.
  passthroughPointerEvents = false,
  // With passthroughPointerEvents: a drag (not a click) on the page draws a
  // region through the draw path. Off by default.
  dragToDraw = false,
  // Draw anchor and region zones dimmed (the "Show anchor and region" toggle off).
  dimAnchorRegion = false,
  // A pick mode (Click a word, identifier pick) is on: zones take no pointer
  // events, and all but pickFieldId's zones are dimmed.
  pickMode = false,
  pickFieldId = null,
}) => {
  const overlayRef = useRef(null);
  const [interaction, setInteraction] = useState(null);
  const [selectedZoneId, setSelectedZoneId] = useState(editingZoneId || null);
  // Drawing state
  const [drawStart, setDrawStart] = useState(null); // { x, y } normalized
  const [drawRect, setDrawRect] = useState(null);   // { x, y, w, h } normalized
  // Right-click menu: { zoneId, x, y } in px within the overlay, or null.
  const [contextMenu, setContextMenu] = useState(null);
  const [hoveredZoneId, setHoveredZoneId] = useState(null);
  const contextMenuRef = useRef(null);
  const { beginDragScroll, endDragScroll } = useContext(DragScrollContext);

  const pageIndex = (currentPageNumber || 1) - 1;
  const visibleZones = zones.filter(z => (z.page_index ?? 0) === pageIndex);

  // Sync selectedZoneId with external editingZoneId prop
  useEffect(() => {
    setSelectedZoneId(editingZoneId || null);
  }, [editingZoneId]);

  // Keyboard handler: Delete/Backspace removes selected zone
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedZoneId) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Don't delete if user is typing in an input
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
        e.preventDefault();
        if (onZoneDelete) onZoneDelete(selectedZoneId);
        setSelectedZoneId(null);
        if (onEditingZoneIdChanged) onEditingZoneIdChanged(null);
      }
      if (e.key === 'Escape') {
        setSelectedZoneId(null);
        if (onEditingZoneIdChanged) onEditingZoneIdChanged(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedZoneId, onZoneDelete, onEditingZoneIdChanged]);

  // Right-click menu closes on Escape or a press anywhere outside it.
  useEffect(() => {
    if (!contextMenu) return undefined;
    const onDown = (e) => {
      if (contextMenuRef.current && contextMenuRef.current.contains(e.target)) return;
      setContextMenu(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setContextMenu(null); };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [contextMenu]);

  // A zone that is no longer shown (deleted, or another page) has no menu.
  useEffect(() => {
    if (contextMenu && !visibleZones.some(z => z.id === contextMenu.zoneId)) setContextMenu(null);
  }, [contextMenu, visibleZones]);

  // ---- Helper: page-normalized coords of a mouse event ----
  // Measured against the page box now, so they hold through scroll and zoom.
  // Unclamped: a point off the page is outside [0, 1].
  const getPagePoint = useCallback((e) => {
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return null;
    return { x: (e.clientX - rect.left) / rect.width, y: (e.clientY - rect.top) / rect.height };
  }, []);

  // The same point clamped to the page.
  const getNormalized = useCallback((e) => {
    const p = getPagePoint(e);
    if (!p) return null;
    return { x: Math.max(0, Math.min(1, p.x)), y: Math.max(0, Math.min(1, p.y)) };
  }, [getPagePoint]);

  // ---- DRAW MODE ----
  //
  // A draw is tracked with listeners added when the pointer goes down and
  // removed when the draw ends, never by an effect keyed on props. The old
  // effect depended on callback props (onModeSwitch is an inline arrow in
  // TemplateEditorPage), and TemplateZoneViewer's own window mouseup listener
  // re-renders this overlay first; React then ran the effect cleanup in the
  // middle of the mouseup dispatch, which removed our mouseup listener before
  // it ran and left the draw stuck to the cursor. The handlers read the
  // latest props through latestRef instead.
  const latestRef = useRef({});
  latestRef.current = { getNormalized, ocrWords, loadWords, onZoneCreate, onModeSwitch, pageIndex, drawZoneType, drawFieldId, beginDragScroll, endDragScroll };
  const drawRef = useRef(null); // { start: {x, y}, detach } while a draw is in progress

  const endDraw = useCallback((e, commit) => {
    const active = drawRef.current;
    if (!active) return;
    drawRef.current = null;
    active.detach();
    latestRef.current.endDragScroll();
    setDrawStart(null);
    setDrawRect(null);
    if (!commit) return;
    // The page the press started on owns the zone and its words.
    const page = active.page;
    const norm = latestRef.current.getNormalized(e);
    const { onZoneCreate: create, onModeSwitch: switchMode, drawZoneType: zoneType, drawFieldId: fieldId, loadWords: load } = latestRef.current;
    // unavailable: the page's words were waited for and never arrived.
    const finish = (words, unavailable = false) => {
      let zone;
      if (active.band) {
        // Without the words the band itself is the zone, never nothing.
        zone = unavailable ? bandRect(active.start, norm) : bandWordsZone(active.start, norm, words);
      } else {
        zone = drawnZone(active.start, norm, words);
      }
      if (!zone) return;
      if (create) create({ ...zone, page_index: page, zone_type: zoneType, field_id: fieldId });
      // Auto-switch to Select mode after creating a zone
      if (switchMode) switchMode('select');
    };
    const words = wordsOnPage(latestRef.current.ocrWords, page);
    if (words != null || !load) { finish(words); return; }
    let timer;
    const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve(null), WORDS_WAIT_MS); });
    Promise.race([Promise.resolve(load(page)).catch(() => null), timeout])
      .then((loaded) => { clearTimeout(timer); finish(wordsOnPage(loaded, page), loaded == null); });
  }, []);

  // band: a Click-a-word drag, previewed and resolved as a band over words.
  // press / current: client points of the press and of the pointer now, for
  // the viewer's edge auto-scroll.
  const beginDraw = useCallback((start, { band = false, press = null, current = press } = {}) => {
    if (drawRef.current) return;
    const toRect = band ? bandRect : previewRect;
    const onMove = (e) => {
      const norm = latestRef.current.getNormalized(e);
      if (!norm) return;
      setDrawRect(toRect(start, norm));
    };
    const onRelease = (e) => endDraw(e, true);
    const onAbort = (e) => endDraw(e, false);
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Esc ends only the draw; a second Esc reaches the page and leaves placement.
      e.preventDefault();
      e.stopPropagation();
      endDraw(e, false);
    };
    const listeners = [
      ['pointermove', onMove], ['mousemove', onMove],
      ['pointerup', onRelease], ['mouseup', onRelease],
      ['pointercancel', onAbort], ['dragend', onAbort], ['blur', onAbort],
    ];
    listeners.forEach(([type, fn]) => window.addEventListener(type, fn));
    window.addEventListener('keydown', onKey, true);
    drawRef.current = {
      start,
      band,
      page: latestRef.current.pageIndex,
      detach: () => {
        listeners.forEach(([type, fn]) => window.removeEventListener(type, fn));
        window.removeEventListener('keydown', onKey, true);
      },
    };
    if (press) latestRef.current.beginDragScroll(press, current);
    setDrawStart(start);
    setDrawRect({ x: start.x, y: start.y, w: 0, h: 0 });
  }, [endDraw]);

  // Unmount mid-draw (page change, leaving the editor): drop the listeners.
  useEffect(() => () => {
    if (drawRef.current) {
      drawRef.current.detach();
      latestRef.current.endDragScroll();
    }
    drawRef.current = null;
  }, []);

  const handleOverlayPointerDown = useCallback((e) => {
    if (mode !== 'draw' || e.button !== 0) return;
    const norm = getNormalized(e);
    if (!norm) return;
    // Keep receiving the pointer while it is outside the overlay or the pane.
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* not capturable */ }
    beginDraw({ x: norm.x, y: norm.y }, { press: e });
  }, [mode, getNormalized, beginDraw]);

  const handleOverlayMouseDown = useCallback((e) => {
    if (mode !== 'draw') {
      // In select mode, clicking background deselects
      if (e.target === overlayRef.current) {
        setSelectedZoneId(null);
        if (onEditingZoneIdChanged) onEditingZoneIdChanged(null);
      }
      return;
    }
    // No text selection or native drag while drawing.
    e.preventDefault();
  }, [mode, onEditingZoneIdChanged]);

  // ---- CLICK-A-WORD MODE: a drag draws a region ----
  //
  // With passthroughPointerEvents the overlay ignores the pointer and word
  // clicks go to OcrWordOverlay. When dragToDraw is also set, a press on this
  // page that moves more than DRAG_TO_DRAW_PX becomes a draw through the same
  // path as draw mode, previewed as a band (bandRect) and saved as the words
  // it touches (bandWordsZone); a press that does not move stays a plain word
  // click.
  useEffect(() => {
    if (!passthroughPointerEvents || !dragToDraw) return undefined;
    let pending = null;
    let suppressClick = false;
    const clearPending = () => {
      if (!pending) return;
      window.removeEventListener('pointermove', pending.onMove, true);
      window.removeEventListener('pointerup', pending.onUp, true);
      window.removeEventListener('pointercancel', pending.onUp, true);
      pending = null;
    };
    const onDown = (e) => {
      const surface = overlayRef.current?.parentElement;
      if (e.button !== 0 || drawRef.current || !surface || !surface.contains(e.target)) return;
      clearPending();
      const origin = { clientX: e.clientX, clientY: e.clientY };
      const onMove = (ev) => {
        if (Math.hypot(ev.clientX - origin.clientX, ev.clientY - origin.clientY) <= DRAG_TO_DRAW_PX) return;
        clearPending();
        const norm = latestRef.current.getNormalized(origin);
        if (!norm) return;
        window.getSelection?.()?.removeAllRanges();
        // The click that follows this press must not also pick a word.
        suppressClick = true;
        beginDraw({ x: norm.x, y: norm.y }, { band: true, press: origin, current: ev });
        setDrawRect(bandRect({ x: norm.x, y: norm.y }, latestRef.current.getNormalized(ev)));
      };
      const onUp = () => clearPending();
      pending = { onMove, onUp };
      window.addEventListener('pointermove', onMove, true);
      window.addEventListener('pointerup', onUp, true);
      window.addEventListener('pointercancel', onUp, true);
    };
    const onClick = (e) => {
      if (!suppressClick) return;
      suppressClick = false;
      e.stopPropagation();
      e.preventDefault();
    };
    const onPointerDownReset = () => { suppressClick = false; };
    window.addEventListener('pointerdown', onPointerDownReset, true);
    window.addEventListener('pointerdown', onDown, true);
    window.addEventListener('click', onClick, true);
    return () => {
      clearPending();
      window.removeEventListener('pointerdown', onPointerDownReset, true);
      window.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('click', onClick, true);
    };
  }, [passthroughPointerEvents, dragToDraw, beginDraw]);

  // ---- SELECT MODE: Move start ----
  const handleMoveStart = useCallback((e, zone) => {
    if (mode === 'draw') return;
    if (selectedZoneId !== zone.id) return;
    e.preventDefault();
    e.stopPropagation();
    const startNorm = getPagePoint(e);
    if (!startNorm) return;
    setInteraction({
      type: 'move',
      zoneId: zone.id,
      order: zonePaintOrder(visibleZones, selectedZoneId).map(z => z.id),
      startNorm,
      startX: zone.x,
      startY: zone.y,
      startW: zone.w,
      startH: zone.h,
    });
    beginDragScroll(e);
  }, [mode, selectedZoneId, visibleZones, getPagePoint, beginDragScroll]);

  // ---- SELECT MODE: Resize start ----
  const handleResizeStart = useCallback((e, zone, handle) => {
    e.preventDefault();
    e.stopPropagation();
    const startNorm = getPagePoint(e);
    if (!startNorm) return;
    setInteraction({
      type: 'resize',
      handle, // 'tl' | 'tr' | 'bl' | 'br'
      zoneId: zone.id,
      order: zonePaintOrder(visibleZones, zone.id).map(z => z.id),
      startNorm,
      startX: zone.x,
      startY: zone.y,
      startW: zone.w,
      startH: zone.h,
    });
    beginDragScroll(e);
  }, [visibleZones, getPagePoint, beginDragScroll]);

  // ---- SELECT MODE: Global mouse move/up for move+resize ----
  useEffect(() => {
    if (!interaction) return;

    const handleMouseMove = (e) => {
      e.preventDefault();
      // The pointer's travel in page units since the press, measured against
      // the page box now (not the press's client pixels), so a zone stays with
      // the pointer while the pages scroll. Unclamped: a zone grabbed by its
      // middle still reaches the page edge; clampZone keeps it on the page.
      const p = getPagePoint(e);
      if (!p) return;
      const dx = p.x - interaction.startNorm.x;
      const dy = p.y - interaction.startNorm.y;

      const zone = zones.find(z => z.id === interaction.zoneId);
      if (!zone) return;

      if (interaction.type === 'move') {
        const clamped = clampZone(interaction.startX + dx, interaction.startY + dy, zone.w, zone.h);
        if (onZoneCommit) onZoneCommit({ ...zone, x: clamped.x, y: clamped.y });
      } else if (interaction.type === 'resize') {
        const h = interaction.handle;
        let nx = interaction.startX;
        let ny = interaction.startY;
        let nw = interaction.startW;
        let nh = interaction.startH;

        if (h === 'br') {
          nw = interaction.startW + dx;
          nh = interaction.startH + dy;
        } else if (h === 'tl') {
          nx = interaction.startX + dx;
          ny = interaction.startY + dy;
          nw = interaction.startW - dx;
          nh = interaction.startH - dy;
        } else if (h === 'tr') {
          ny = interaction.startY + dy;
          nw = interaction.startW + dx;
          nh = interaction.startH - dy;
        } else if (h === 'bl') {
          nx = interaction.startX + dx;
          nw = interaction.startW - dx;
          nh = interaction.startH + dy;
        }

        const clamped = clampZone(nx, ny, nw, nh);
        if (onZoneCommit) onZoneCommit({ ...zone, ...clamped });
      }
    };

    const handleMouseUp = () => {
      // Snap to the zone's own page's OCR words on mouse-up
      const zone = zones.find(z => z.id === interaction.zoneId);
      const pageWords = zone ? wordsOnPage(ocrWords, zone.page_index ?? 0) : null;
      if (pageWords && pageWords.length > 0) {
        if (onZoneCommit) {
          const snapped = snapToOcrWords(zone, pageWords);
          onZoneCommit({ ...zone, ...snapped });
        }
      }
      endDragScroll();
      setInteraction(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [interaction, zones, onZoneCommit, ocrWords, getPagePoint, endDragScroll]);

  const isInteracting = !!interaction || !!drawStart;

  // Click on zone: select it
  const handleZoneClick = useCallback((e, zone) => {
    e.stopPropagation();
    if (mode === 'draw') return;
    const newId = selectedZoneId === zone.id ? null : zone.id;
    setSelectedZoneId(newId);
    if (onEditingZoneIdChanged) onEditingZoneIdChanged(newId);
    // Also notify parent field selection
    const parentField = fields.find(f => f.id === zone.field_id);
    if (parentField && onSelectField) onSelectField(parentField);
  }, [mode, selectedZoneId, onEditingZoneIdChanged, fields, onSelectField]);

  // Right-click on zone: select it and open the "Delete zone" menu at the cursor.
  const handleZoneContextMenu = useCallback((e, zone) => {
    if (mode === 'draw') return;
    e.preventDefault();
    e.stopPropagation();
    setSelectedZoneId(zone.id);
    if (onEditingZoneIdChanged) onEditingZoneIdChanged(zone.id);
    const rect = overlayRef.current?.getBoundingClientRect();
    setContextMenu({
      zoneId: zone.id,
      x: rect ? e.clientX - rect.left : 0,
      y: rect ? e.clientY - rect.top : 0,
    });
  }, [mode, onEditingZoneIdChanged]);

  const handleContextDelete = useCallback(() => {
    const zoneId = contextMenu?.zoneId;
    setContextMenu(null);
    if (!zoneId) return;
    setSelectedZoneId(null);
    if (onEditingZoneIdChanged) onEditingZoneIdChanged(null);
    if (onZoneDelete) onZoneDelete(zoneId);
  }, [contextMenu, onZoneDelete, onEditingZoneIdChanged]);

  const contextZone = contextMenu ? visibleZones.find(z => z.id === contextMenu.zoneId) : null;

  // Paint order; frozen for the length of a move or resize so the zone being
  // dragged does not jump in the stack as its area changes.
  let paintedZones;
  if (interaction?.order) {
    const rank = new Map(interaction.order.map((id, i) => [id, i]));
    paintedZones = [...visibleZones].sort(
      (a, b) => (rank.get(a.id) ?? rank.size) - (rank.get(b.id) ?? rank.size)
    );
  } else {
    paintedZones = zonePaintOrder(visibleZones, selectedZoneId);
  }
  // No zone takes the pointer while drawing or picking.
  const inert = mode === 'draw' || pickMode;

  return (
    <div
      ref={overlayRef}
      className="zone-editor-overlay"
      tabIndex={-1}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: passthroughPointerEvents ? 'none' : (isInteracting || mode === 'draw' ? 'auto' : 'none'),
        zIndex: 15,
        cursor: passthroughPointerEvents ? 'default' : (mode === 'draw' ? 'crosshair' : 'default'),
        touchAction: mode === 'draw' && !passthroughPointerEvents ? 'none' : undefined,
        outline: 'none',
      }}
      onPointerDown={handleOverlayPointerDown}
      onMouseDown={handleOverlayMouseDown}
    >
      {/* Existing zones */}
      {paintedZones.map(zone => {
        const style = ZONE_STYLES[zone.zone_type] || ZONE_STYLES.field;
        const outline = isOutlineZone(zone);
        const hit = inert ? 'none' : 'auto';
        const isSelected = selectedZoneId === zone.id;
        const isFieldSelected = selectedField && zones.some(
          z => z.id === zone.id && z.field_id === selectedField.id
        );
        const label = zoneLabel(zone, fields);
        const dimmed = isZoneDimmed(zone, {
          dimAnchorRegion,
          selectedZoneId,
          selectedFieldId: selectedField?.id ?? null,
          pickMode,
          pickFieldId,
        });
        const baseOpacity = isFieldSelected || isSelected || !selectedField ? 1 : 0.3;
        const showLabel = isZoneLabelShown(zone, {
          dimAnchorRegion,
          selectedZoneId,
          selectedFieldId: selectedField?.id ?? null,
          hoveredZoneId,
        });

        return (
          <div
            key={zone.id}
            className="zone-box"
            data-zone-id={zone.id}
            data-zone-type={zone.zone_type}
            data-dimmed={dimmed ? 'true' : undefined}
            data-outline={outline ? 'true' : undefined}
            style={{
              position: 'absolute',
              left: `${zone.x * 100}%`,
              top: `${zone.y * 100}%`,
              width: `${zone.w * 100}%`,
              height: `${zone.h * 100}%`,
              background: outline ? 'transparent' : (isSelected ? style.bg.replace(/[\d.]+\)$/, '0.3)') : style.bg),
              border: `${isSelected ? 2 : 1}px ${isSelected ? 'solid' : 'dashed'} ${style.border}`,
              borderRadius: '2px',
              // An outline zone's inside passes the pointer through; its edge
              // strips and label tab (below) take it and bubble here.
              pointerEvents: outline ? 'none' : hit,
              cursor: isSelected && !inert ? 'move' : 'pointer',
              opacity: dimmed ? Math.min(baseOpacity, DIMMED_OPACITY) : baseOpacity,
              transition: 'opacity 0.15s',
              boxShadow: isSelected ? `0 0 0 2px ${style.border}44` : 'none',
            }}
            onMouseDown={(e) => {
              if (mode === 'draw') return;
              // A right-click opens the menu; it must not start a move (whose
              // mouseup would snap the zone and mark it edited).
              if (e.button !== 0) return;
              handleMoveStart(e, zone);
            }}
            onClick={(e) => handleZoneClick(e, zone)}
            onContextMenu={(e) => handleZoneContextMenu(e, zone)}
            onMouseEnter={() => setHoveredZoneId(zone.id)}
            onMouseLeave={() => setHoveredZoneId(prev => (prev === zone.id ? null : prev))}
            title={`${label}${isSelected ? ' (drag to move, right-click to delete)' : ''}`}
          >
            {/* Outline zones: clickable strips along the border. */}
            {outline && EDGE_DEFS.map(ed => (
              <div
                key={ed.name}
                className="zone-edge"
                data-edge={ed.name}
                style={{ position: 'absolute', ...ed.style, pointerEvents: hit, cursor: isSelected && !inert ? 'move' : 'pointer' }}
              />
            ))}

            {/* Zone label — compact pill above zone (anchor/region: see isZoneLabelShown).
                On an outline zone it is a tab that selects the zone. */}
            {showLabel && (
              <div className={outline ? 'zone-label zone-label-tab' : 'zone-label'} style={{
                position: 'absolute',
                top: '-16px',
                left: '0',
                fontSize: '9px',
                fontWeight: 600,
                color: style.border,
                background: 'rgba(255,255,255,0.92)',
                padding: '1px 5px',
                borderRadius: '3px',
                border: `1px solid ${style.border}44`,
                lineHeight: '13px',
                whiteSpace: 'nowrap',
                pointerEvents: outline ? hit : 'none',
                cursor: outline && !inert ? 'pointer' : undefined,
                maxWidth: '180px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {label}
              </div>
            )}

            {/* 4-corner resize handles — only when selected */}
            {isSelected && !inert && HANDLE_DEFS.map(hd => (
              <div
                key={hd.name}
                onMouseDown={(e) => handleResizeStart(e, zone, hd.name)}
                style={{
                  position: 'absolute',
                  [hd.hProp]: `-${HANDLE_SIZE / 2}px`,
                  [hd.vProp]: `-${HANDLE_SIZE / 2}px`,
                  width: `${HANDLE_SIZE}px`,
                  height: `${HANDLE_SIZE}px`,
                  background: style.border,
                  border: '1px solid #fff',
                  borderRadius: '2px',
                  cursor: hd.cursor,
                  zIndex: 2,
                  pointerEvents: 'auto',
                }}
              />
            ))}
          </div>
        );
      })}

      {/* Right-click menu */}
      {contextMenu && contextZone && (
        <div
          ref={contextMenuRef}
          role="menu"
          aria-label={`Zone ${zoneLabel(contextZone, fields)}`}
          className="zone-context-menu"
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'absolute',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            zIndex: 30,
            pointerEvents: 'auto',
            minWidth: '150px',
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '4px 0',
            fontSize: '12px',
          }}
        >
          <div style={{ padding: '4px 10px', color: '#6b7280', fontSize: '11px', whiteSpace: 'nowrap' }}>
            {zoneLabel(contextZone, fields)}
          </div>
          <button
            type="button"
            role="menuitem"
            autoFocus
            onClick={handleContextDelete}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '6px 10px', border: 'none', background: 'none',
              color: '#b91c1c', cursor: 'pointer', fontSize: '12px',
            }}
          >
            Delete zone
          </button>
        </div>
      )}

      {/* Draw preview rectangle */}
      {drawRect && drawRect.w >= MIN_ZONE && drawRect.h >= MIN_ZONE && (
        <div
          style={{
            position: 'absolute',
            left: `${drawRect.x * 100}%`,
            top: `${drawRect.y * 100}%`,
            width: `${drawRect.w * 100}%`,
            height: `${drawRect.h * 100}%`,
            background: 'rgba(124, 58, 237, 0.15)',
            border: '2px dashed #7c3aed',
            borderRadius: '2px',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};

export default ZoneEditorOverlay;
